/**
 * Application-wide constants
 * Centralized configuration values to avoid magic numbers and strings
 */

// ============================================================================
// TIMEOUTS (in milliseconds)
// ============================================================================

export const TIMEOUTS = {
  MESH_GENERATION: 1800000,
  MESH_DATA_FETCH: 60000,
  STATUS_POLL_INTERVAL: 2000,
  VALIDATION_POLL_INTERVAL: 5000,
  DASHBOARD_POLL_INTERVAL: 5000,
  STUDY_POLL_INTERVAL: 5000,
  COPY_FEEDBACK_DURATION: 1000,
  /** Treat a presigned S3 URL as stale after 10 min.
   *
   * They do NOT expire after 15 min — the backend signs every download with
   * `DOWNLOAD_URL_EXPIRY = 3600` (KhoriumBackend job/common/s3.py), measured on
   * a live URL as dying between 3554 s and 3615 s. The 10 min here is simply
   * conservative, and deliberately left alone: eight call sites refetch on it
   * and being early is what keeps most of them from ever meeting an expired
   * credential. Raising it needs its own change. See `useResiduals`, which
   * stopped using this constant because it handles expiry directly. */
  PRESIGNED_URL_STALE: 10 * 60 * 1000,
} as const;

// ============================================================================
// GEOMETRY UPLOAD
// ============================================================================

/**
 * Accepted geometry upload types + size cap — the backend ingests STEP/STP/OBJ
 * and STL (internal-flow + external/FEA via the STL bypass path) up to 1 GB.
 * Single source of truth for every geometry dropzone's react-dropzone config.
 * The MeshGen and Diagnosis library dropzones consume these;
 * `GeometryDropzone` (SimOps) still inlines its own copy and should migrate
 * here (see SimOps files/AGENTS.md).
 */
export const GEOMETRY_UPLOAD_ACCEPT: Record<string, string[]> = {
  'application/octet-stream': ['.step', '.stp', '.obj', '.stl'],
};
export const GEOMETRY_UPLOAD_MAX_BYTES = 1024 * 1024 * 1024; // 1 GB

// ============================================================================
// STRATEGY FILTERS
// ============================================================================

export const STRATEGY_FILTERS = {
  EXCLUDED_NAMES: ['gpu', 'polyhedral', 'bcc', 'lattice'] as const,
  shouldExclude(strategyName: string): boolean {
    const lower = strategyName.toLowerCase();
    return STRATEGY_FILTERS.EXCLUDED_NAMES.some((excluded) =>
      lower.includes(excluded),
    );
  },
};

// ============================================================================
// MESH PRESETS
// ============================================================================

export interface MinMax {
  min: number;
  max: number;
}

export interface GeometryCharacteristics {
  diagonal: number;
  /** max(dx, dy, dz) — industry-standard characteristic length */
  maxDimension: number;
  /** min(dx, dy, dz) — used to detect thin-walled geometry */
  minDimension: number;
}

/**
 * Tuning constants for geometry-specific preset clamping.
 * Named here so they can be updated when real mesh-quality data is available.
 */
export const PRESET_TUNING = {
  /** min_size is always max_size / this ratio (matches existing preset proportions). */
  MIN_MAX_RATIO: 5,
} as const;

/**
 * Minimum elements across the smallest geometry dimension, per preset.
 *
 * Caps minSize so the global Gmsh floor can resolve thin features (e.g. a
 * 2mm-thick plate needs ≤ 0.25mm elements for Fine to get 8 elements across).
 * Applies to all geometries — for non-thin bodies the cap exceeds the
 * ratio-derived minSize and has no effect.
 * Values double with each tier, matching the ~2× resolution step between presets.
 */
const PRESET_ELEMENTS_ACROSS_MIN: Record<string, number> = {
  Coarse: 2,
  Medium: 4,
  Fine: 8,
  'Very Fine': 16,
  'Ultra Fine': 32,
};

/**
 * Preset ratios — multiplied by (maxDimension / 100) to get mm values.
 * Uses max(dx,dy,dz) as the characteristic length, matching industry convention
 * (OpenFOAM, SimScale, Flow360) and the backend's _auto_scale_sizes.
 */
export const MESH_PRESET_RATIOS = {
  COARSE: { min: 5.0, max: 25.0 },
  MEDIUM: { min: 2.0, max: 10.0 },
  FINE: { min: 1.0, max: 5.0 },
  VERY_FINE: { min: 0.5, max: 1.0 },
  ULTRA_FINE: { min: 0.25, max: 0.75 },
} as const satisfies Record<string, MinMax>;

export const PRESET_RATIOS: Record<string, MinMax> = {
  Coarse: MESH_PRESET_RATIOS.COARSE,
  Medium: MESH_PRESET_RATIOS.MEDIUM,
  Fine: MESH_PRESET_RATIOS.FINE,
  'Very Fine': MESH_PRESET_RATIOS.VERY_FINE,
  'Ultra Fine': MESH_PRESET_RATIOS.ULTRA_FINE,
};

/**
 * Compute recommended preset sizes from geometry characteristics.
 *
 * Uses max(dx,dy,dz) as the characteristic length (industry standard).
 * Each preset caps minSize to minDimension / PRESET_ELEMENTS_ACROSS_MIN[preset]
 * so Gmsh's global floor can resolve thin features. maxSize is always the
 * ratio-derived value — local refinement near thin entities is handled by the
 * backend's geometry_sizing.py Fields. For non-thin geometry the cap exceeds
 * the ratio-derived minSize and has no effect.
 */
export function computePresetSizes(
  geo: GeometryCharacteristics,
): Record<string, MinMax> {
  const base = geo.maxDimension / 100;

  const result: Record<string, MinMax> = {};
  for (const [name, ratio] of Object.entries(PRESET_RATIOS)) {
    const maxSize = parseFloat((ratio.max * base).toPrecision(3));
    let minSize = parseFloat((ratio.min * base).toPrecision(3));

    const n = PRESET_ELEMENTS_ACROSS_MIN[name];
    if (n !== undefined) {
      const minDimCap = parseFloat((geo.minDimension / n).toPrecision(3));
      if (minDimCap < minSize) {
        minSize = minDimCap;
      }
    }

    result[name] = { min: minSize, max: maxSize };
  }
  return result;
}

// ============================================================================
// QUALITY PRESETS
// ============================================================================

export const QUALITY_PRESETS = [
  'Coarse',
  'Medium',
  'Fine',
  'Very Fine',
  'Ultra Fine',
  'Custom',
] as const;

// ============================================================================
// DEFAULT VALUES
// ============================================================================

// ============================================================================
// MESH STRATEGIES
// ============================================================================

/** Display name → API strategy value */
export const MESH_STRATEGIES = {
  'Tetrahedral (Fast)': 'tetrahedral_hxt',
  'Cartesian (cfMesh)': 'cfmesh_cartesian',
  'Polyhedral (cfMesh)': 'cfmesh_poly',
} as const satisfies Record<string, string>;

export type MeshStrategyName = keyof typeof MESH_STRATEGIES;

/**
 * Which explicit strategies are selectable per element type. "Auto" (null) is
 * always offered separately and omitted here. cfMesh produces both hex
 * (cartesianMesh) and poly (pMesh) meshes; HXT produces tet — each strategy
 * appears only under the element type it emits.
 *
 * Invariant: every `MESH_STRATEGIES` key must appear in exactly one bucket.
 * Now that a 3rd strategy has landed the two-parallel-map design is at its
 * limit — a 4th should fold element-type onto the strategy definition (one
 * record: name → { apiValue, elementType }) instead.
 */
export const MESH_STRATEGIES_BY_ELEMENT: Record<
  'tet' | 'hex' | 'poly',
  readonly MeshStrategyName[]
> = {
  tet: ['Tetrahedral (Fast)'],
  hex: ['Cartesian (cfMesh)'],
  poly: ['Polyhedral (cfMesh)'],
};

export const DOMAIN_DEFAULTS = {
  UPSTREAM_FACTOR: 3.0,
  DOWNSTREAM_FACTOR: 5.0,
  LATERAL_Y_FACTOR: 3.0,
  LATERAL_Z_FACTOR: 3.0,
} as const;

export type DirectionKey =
  | 'x_neg'
  | 'x_pos'
  | 'y_neg'
  | 'y_pos'
  | 'z_neg'
  | 'z_pos';

export const ALL_DIRECTION_KEYS: readonly DirectionKey[] = [
  'x_neg',
  'x_pos',
  'y_neg',
  'y_pos',
  'z_neg',
  'z_pos',
] as const;

// Resolve 6 per-direction factors from DomainParams, mirroring the backend's
// `DomainParams.resolved` property. Per-direction overrides take priority;
// missing overrides fall back to legacy fields mapped through flow_axis.
export function resolveDirectionFactors(dp: {
  flow_axis?: 'x' | 'y' | 'z';
  upstream_factor?: number;
  downstream_factor?: number;
  lateral_y_factor?: number;
  lateral_z_factor?: number;
  x_neg_factor?: number | null;
  x_pos_factor?: number | null;
  y_neg_factor?: number | null;
  y_pos_factor?: number | null;
  z_neg_factor?: number | null;
  z_pos_factor?: number | null;
}): Record<DirectionKey, number> {
  const up = dp.upstream_factor ?? DOMAIN_DEFAULTS.UPSTREAM_FACTOR;
  const dn = dp.downstream_factor ?? DOMAIN_DEFAULTS.DOWNSTREAM_FACTOR;
  const latY = dp.lateral_y_factor ?? DOMAIN_DEFAULTS.LATERAL_Y_FACTOR;
  const latZ = dp.lateral_z_factor ?? DOMAIN_DEFAULTS.LATERAL_Z_FACTOR;
  const defaults: Record<
    'x' | 'y' | 'z',
    [number, number, number, number, number, number]
  > = {
    x: [up, dn, latY, latY, latZ, latZ],
    y: [latY, latY, up, dn, latZ, latZ],
    z: [latY, latY, latZ, latZ, up, dn],
  };
  const d = defaults[dp.flow_axis ?? 'x'];
  const overrides = [
    dp.x_neg_factor,
    dp.x_pos_factor,
    dp.y_neg_factor,
    dp.y_pos_factor,
    dp.z_neg_factor,
    dp.z_pos_factor,
  ];
  return {
    x_neg: overrides[0] ?? d[0],
    x_pos: overrides[1] ?? d[1],
    y_neg: overrides[2] ?? d[2],
    y_pos: overrides[3] ?? d[3],
    z_neg: overrides[4] ?? d[4],
    z_pos: overrides[5] ?? d[5],
  };
}

export const DOMAIN_MIN_FACTOR = 0.5;

export const DEFAULTS = {
  QUALITY_PRESET: 'Medium',
  MAX_ELEMENT_SIZE: 10.0,
  MIN_ELEMENT_SIZE: 2.0,
  // '2' (C3D10) because the store opens on FEA + tet (SIMULATION_TYPE,
  // ELEMENT_TYPE below), and a linear tet reads bending stress low. Picking
  // CFD or CHT resets it to '1' in MeshSettingsPanel.
  ELEMENT_ORDER: '2',
  MESH_STRATEGY: null as MeshStrategyName | null,
  MESH_TYPE: '3d',
  ELEMENT_TYPE: 'tet' as const,
  SIMULATION_TYPE: 'fea' as const,
  BOUNDARY_LAYER: {
    ENABLED: false,
    LAYERS: 5,
    RATIO: 1.2,
    THICKNESS_USER: 0.05,
  },
  STL: {
    BEFORE_MESH_ENABLED: false,
    SURFACE_FACTOR: 0.5,
    CURVATURE_PTS: 24,
    MIN_CIRCLE_NODES: 12,
  },
  ANSYS_MODE: 'None',
  CURVATURE_ADAPTIVE: false,
  COLOR_MODE: 'solid',
  QUALITY_METRIC: 'sicn',
  SHOW_AXES: true,
  CONSOLE_OPEN: true,
} as const;

// ============================================================================
// PROGRESS STEPS
// ============================================================================

export const PROGRESS_STEPS = {
  STRATEGY: 'strategy',
  CANARY: 'canary',
  LOAD_GEOMETRY: 'load_geometry',
  BOUNDARY_LAYER: 'boundary_layer',
  MESH_1D: '1d',
  MESH_2D: '2d',
  MESH_3D: '3d',
  REFINEMENT: 'refinement',
  OPTIMIZE: 'optimize',
  ORDER2: 'order2',
  QUALITY: 'quality',
} as const;

// ============================================================================
// COLOR MODES
// ============================================================================

export const COLOR_MODES = {
  SOLID: 'solid',
  QUALITY: 'quality',
  GRADIENT: 'gradient',
} as const;

// ============================================================================
// CAD VIEW MODES
// ============================================================================

export const CAD_VIEW_MODES = {
  SURFACE: 'surface',
  WIREFRAME: 'wireframe',
} as const;

// ============================================================================
// MESH TYPES
// ============================================================================

export const MESH_TYPES = {
  SURFACE: '2d',
  VOLUME: '3d',
} as const;

// ============================================================================
// ANSYS EXPORT FORMATS
// ============================================================================

export const ANSYS_FORMATS = {
  FLUENT: 'fluent',
  MECHANICAL: 'mechanical',
} as const;

// ============================================================================
// VALIDATION
// ============================================================================

export const VALIDATION = {
  MIN_PASSWORD_LENGTH: 8,
  MIN_ELEMENT_SIZE_VALUE: 1e-7,
  MAX_ELEMENT_SIZE_VALUE: 1000,
  MAX_CONSECUTIVE_404: 3,
} as const;

// ============================================================================
// UI CONSTANTS
// ============================================================================

export const UI = {
  CONSOLE_HEIGHT: 224,
  CONSOLE_HEADER_HEIGHT: 32,
  WORKSPACE_SLIDE_DURATION_MS: 300,
} as const;

// ============================================================================
// QUALITY METRIC INFO
// ============================================================================

export const METRIC_INFO: Record<
  string,
  {
    label: string;
    ideal: string;
    target: string;
    isGood: (stats: { min: number; max: number }) => boolean;
  }
> = {
  sicn: {
    label: 'SICN',
    ideal: '1',
    target: 'min > 0.5',
    isGood: (s) => s.min > 0.5,
  },
  skewness: {
    label: 'Skewness',
    ideal: '0',
    target: 'max < 0.5',
    isGood: (s) => s.max < 0.5,
  },
  gamma: {
    label: 'Gamma',
    ideal: '1',
    target: 'min > 0.4',
    isGood: (s) => s.min > 0.4,
  },
  min_angle: {
    label: 'Min Angle',
    ideal: '\u03C0/2 rad',
    target: 'min > 0.17 rad',
    isGood: (s) => s.min > 0.17,
  },
  aspect_ratio: {
    label: 'Aspect Ratio',
    ideal: '1',
    target: 'max < 3',
    isGood: (s) => s.max < 3,
  },
  non_orthogonality: {
    label: 'Non-Orthogonality',
    ideal: '0°',
    target: 'max < 70°',
    isGood: (s) => s.max < 70,
  },
};
